import { useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { ExternalLink } from '@components/Link';
import { Typography } from '@components/Typography';
import MarkdownRenderer, { isMarkdownContent } from '@components/MarkdownRenderer';
import { useTranslation } from '@hooks';
import { renderTextWithLinks } from '@helpers/parse-link';
import { MessageContentType, MessageStatus } from '@constants/inbox';
import { FileIcon } from 'assets/images/icons';

import 'react-h5-audio-player/lib/styles.css';

const ImageBlurWrapper = styled('div')(() => ({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 80,
    height: 60,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    pointerEvents: 'none',
    '& .MuiCircularProgress-root': {
        color: 'rgba(0, 0, 0, 0.38) !important',
    },
}));

const StyledMarkdownRenderer = styled(MarkdownRenderer)({
    maxWidth: 'calc(300px - 24px)',
});

const StyledAudioPlayer = styled(AudioPlayer)({
    width: 250,
    boxShadow: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    '& .rhap_time': {
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: 12,
        fontFamily: 'Rubik, sans-serif',
        width: 34,
    },
    '& .rhap_progress-indicator': {
        width: 16,
        height: 16,
        top: -6,
    },
    '& .rhap_controls-section': {
        flex: 1,
    },
    '& .rhap_play-pause-button': {
        width: 32,
        height: 32,
        fontSize: 32,
    },
    '& svg': {
        opacity: 1,
        '& path': {
            fill: '#868686 !important',
        },
    },
});

const MessageContent = ({ data, isOutgoing }) => {
    const [isStoryError, setIsStoryError] = useState(false);

    const { t } = useTranslation();

    return data.status === MessageStatus.Deleted ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic !important' }}>
            {t('InboxPage.messageDeleted')}
        </Typography>
    ) : (
        <>
            {data.contentType === MessageContentType.Text ? (
                isMarkdownContent(data.content) ? (
                    <StyledMarkdownRenderer content={data.content} />
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {renderTextWithLinks(data.content)}
                    </Typography>
                )
            ) : null}
            {data.contentType === MessageContentType.Image ? (
                <ExternalLink
                    href={data.content}
                    underline="none"
                    sx={{
                        position: 'relative',
                        pointerEvents: isOutgoing && data.status === MessageStatus.Sent ? 'none' : 'auto',
                    }}
                >
                    {data.status === MessageStatus.Sent ? (
                        <ImageBlurWrapper>
                            <CircularProgress />
                        </ImageBlurWrapper>
                    ) : null}
                    <img width={80} height={60} style={{ objectFit: 'contain' }} src={data.content} alt="download" />
                </ExternalLink>
            ) : null}
            {data.contentType === MessageContentType.StoryImage ? (
                !isStoryError ? (
                    <ExternalLink href={data.content} underline="none">
                        <img
                            width={80}
                            height={60}
                            style={{ objectFit: 'contain' }}
                            onError={() => setIsStoryError(true)}
                            src={data.content}
                            alt="download"
                        />
                    </ExternalLink>
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {t('InboxPage.storyNotAvailable')}
                    </Typography>
                )
            ) : null}
            {data.contentType === MessageContentType.Video ? (
                <ExternalLink href={data.content} underline="none" sx={{ display: 'flex' }}>
                    <video preload="metadata" style={{ width: 80, height: 60, maxWidth: '100%' }} src={data.content} />
                </ExternalLink>
            ) : null}
            {data.contentType === MessageContentType.StoryVideo ? (
                !isStoryError ? (
                    <ExternalLink href={data.content} underline="none" sx={{ display: 'flex' }}>
                        <video
                            preload="metadata"
                            style={{ width: 80, height: 60, maxWidth: '100%' }}
                            src={data.content}
                            onError={() => setIsStoryError(true)}
                        />
                    </ExternalLink>
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {t('InboxPage.storyNotAvailable')}
                    </Typography>
                )
            ) : null}
            {data.contentType === MessageContentType.Audio ? (
                <StyledAudioPlayer
                    src={data.content}
                    preload="metadata"
                    layout="horizontal-reverse"
                    showJumpControls={false}
                    customAdditionalControls={[]}
                    customVolumeControls={[]}
                />
            ) : null}
            {data.contentType === MessageContentType.File ? (
                <ExternalLink href={data.content} underline="none" sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <FileIcon />
                    <Typography
                        variant="body2"
                        sx={{ display: 'inline-block', marginLeft: '4px', color: 'rgba(0, 0, 0, 0.87)' }}
                    >
                        {t('common.file')}
                    </Typography>
                </ExternalLink>
            ) : null}
        </>
    );
};

export default MessageContent;
